/**
 * Logging utility with configurable log levels
 * 
 * Log levels:
 * - ERROR: Always shown (use sparingly, avoid sensitive data)
 * - WARN: Shown in development and when log level allows
 * - INFO: Shown in development and when log level allows
 * - DEBUG: Shown only when debug mode is enabled
 * 
 * Configure via environment variables:
 * - REACT_APP_LOG_LEVEL: 'error' | 'warn' | 'info' | 'debug' (default: 'error' in production, 'info' in development)
 * - REACT_APP_DEBUG: 'true' to enable debug logs
 */

interface LogLevel {
  log: (...args: any[]) => void;
  error: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  info: (...args: any[]) => void;
  debug: (...args: any[]) => void;
}

enum LogLevelEnum {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

class Logger implements LogLevel {
  private isDevelopment: boolean;
  private isDebugEnabled: boolean;
  private prefix: string;
  private logLevel: LogLevelEnum;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isDebugEnabled = process.env.REACT_APP_DEBUG === 'true';
    this.prefix = '[Phoenix]';
    
    // Check localStorage first for persisted log level
    const storedLogLevel = typeof window !== 'undefined' ? 
      localStorage.getItem('PHOENIX_LOG_LEVEL')?.toLowerCase() : null;
    
    // Set log level from localStorage, then environment, then defaults
    const logLevel = storedLogLevel || process.env.REACT_APP_LOG_LEVEL?.toLowerCase();
    
    if (logLevel === 'error') {
      this.logLevel = LogLevelEnum.ERROR;
    } else if (logLevel === 'warn') {
      this.logLevel = LogLevelEnum.WARN;
    } else if (logLevel === 'info') {
      this.logLevel = LogLevelEnum.INFO;
    } else if (logLevel === 'debug') {
      this.logLevel = LogLevelEnum.DEBUG;
    } else {
      // Default: error-only in production, info in development
      this.logLevel = this.isDevelopment ? LogLevelEnum.INFO : LogLevelEnum.ERROR;
    }
  }

  private shouldLog(level: LogLevelEnum): boolean {
    return this.logLevel >= level;
  }

  /**
   * Log general messages (same as info)
   */
  log(...args: any[]): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.log(this.prefix, ...args);
    }
  }

  /**
   * Log error messages (always shown when level allows)
   * Note: Be careful not to log sensitive data in errors
   */
  error(...args: any[]): void {
    if (this.shouldLog(LogLevelEnum.ERROR)) {
      console.error(this.prefix, '[ERROR]', ...args);
    }
  }

  /**
   * Log warning messages
   */
  warn(...args: any[]): void {
    if (this.shouldLog(LogLevelEnum.WARN)) {
      console.warn(this.prefix, '[WARN]', ...args);
    }
  }

  /**
   * Log info messages
   */
  info(...args: any[]): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.info(this.prefix, '[INFO]', ...args);
    }
  }

  /**
   * Log debug messages
   */
  debug(...args: any[]): void {
    if (this.shouldLog(LogLevelEnum.DEBUG)) {
      console.debug(this.prefix, '[DEBUG]', ...args);
    }
  }

  /**
   * Log performance metrics (info level)
   */
  time(label: string): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.time(`${this.prefix} ${label}`);
    }
  }

  timeEnd(label: string): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.timeEnd(`${this.prefix} ${label}`);
    }
  }

  /**
   * Log table data (info level)
   */
  table(data: any): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.table(data);
    }
  }

  /**
   * Group related logs (info level)
   */
  group(label: string): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.group(`${this.prefix} ${label}`);
    }
  }

  groupEnd(): void {
    if (this.shouldLog(LogLevelEnum.INFO)) {
      console.groupEnd();
    }
  }

  /**
   * Create a logger with a specific module name
   */
  module(moduleName: string): LogLevel {
    const modulePrefix = `${this.prefix}[${moduleName}]`;
    return {
      log: (...args: any[]) => {
        if (this.shouldLog(LogLevelEnum.INFO)) {
          console.log(modulePrefix, ...args);
        }
      },
      error: (...args: any[]) => {
        if (this.shouldLog(LogLevelEnum.ERROR)) {
          console.error(modulePrefix, '[ERROR]', ...args);
        }
      },
      warn: (...args: any[]) => {
        if (this.shouldLog(LogLevelEnum.WARN)) {
          console.warn(modulePrefix, '[WARN]', ...args);
        }
      },
      info: (...args: any[]) => {
        if (this.shouldLog(LogLevelEnum.INFO)) {
          console.info(modulePrefix, '[INFO]', ...args);
        }
      },
      debug: (...args: any[]) => {
        if (this.shouldLog(LogLevelEnum.DEBUG)) {
          console.debug(modulePrefix, '[DEBUG]', ...args);
        }
      }
    };
  }

  /**
   * Set log level at runtime
   * @param level 'error' | 'warn' | 'info' | 'debug'
   */
  setLogLevel(level: 'error' | 'warn' | 'info' | 'debug'): void {
    switch (level) {
      case 'error':
        this.logLevel = LogLevelEnum.ERROR;
        break;
      case 'warn':
        this.logLevel = LogLevelEnum.WARN;
        break;
      case 'info':
        this.logLevel = LogLevelEnum.INFO;
        break;
      case 'debug':
        this.logLevel = LogLevelEnum.DEBUG;
        break;
    }
    console.info(`${this.prefix} Log level set to: ${level}`);
    
    // Store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('PHOENIX_LOG_LEVEL', level);
    }
  }

  /**
   * Get current log level
   */
  getLogLevel(): string {
    const levels = ['error', 'warn', 'info', 'debug'];
    return levels[this.logLevel] || 'unknown';
  }

  /**
   * Enable/disable debug mode at runtime (legacy method, now sets log level)
   */
  setDebugMode(enabled: boolean): void {
    this.setLogLevel(enabled ? 'debug' : 'info');
  }

  /**
   * Check if debug mode is enabled
   */
  isDebugMode(): boolean {
    return this.logLevel >= LogLevelEnum.DEBUG;
  }
}

// Create singleton instance
const logger = new Logger();

// Expose logger globally in development/debug mode for console access
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).logger = logger;
}

// Export default logger
export default logger;

// Export the main logger instance for backward compatibility
export { logger };

// Export convenience functions
export const log = logger.log.bind(logger);
export const error = logger.error.bind(logger);
export const warn = logger.warn.bind(logger);
export const info = logger.info.bind(logger);
export const debug = logger.debug.bind(logger);

// Export utility functions
export const setDebugMode = logger.setDebugMode.bind(logger);
export const isDebugMode = logger.isDebugMode.bind(logger);
export const setLogLevel = logger.setLogLevel.bind(logger);
export const getLogLevel = logger.getLogLevel.bind(logger);

// Usage examples:
// import logger from '@/utils/logger';
// logger.log('This only shows when log level is info or higher');
// logger.error('This shows when log level is error or higher');
// 
// Set log level at runtime:
// logger.setLogLevel('error'); // Only show errors
// logger.setLogLevel('warn');  // Show errors and warnings
// logger.setLogLevel('info');  // Show errors, warnings, and info
// logger.setLogLevel('debug'); // Show everything
//
// Or via environment variables:
// REACT_APP_LOG_LEVEL=error npm start
//
// Or use destructured imports:
// import { log, error, setLogLevel } from '@/utils/logger';
// setLogLevel('error'); // Configure to only show errors
// log('This will not show');
// error('This will show');
//
// Or create module-specific loggers:
// const authLogger = logger.module('Auth');
// authLogger.log('User logged in');